import React from 'react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const team = [
    {
      name: 'Michael Chen',
      role: 'CEO & Founder',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
      bio: 'Former Google engineer with 15+ years building scalable systems.'
    },
    {
      name: 'Sarah Rodriguez',
      role: 'CTO',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop&crop=face',
      bio: 'Ex-Amazon tech lead specializing in cloud architecture.'
    },
    {
      name: 'David Kim',
      role: 'VP of Engineering',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face',
      bio: 'Built engineering teams at three successful startups.'
    },
    {
      name: 'Emily Watson',
      role: 'Head of Design',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face',
      bio: 'Award-winning designer with a passion for user experience.'
    }
  ];

  const values = [
    {
      icon: 'diamond',
      title: 'Excellence',
      description: 'We don\'t settle for "good enough." Every line of code, every design decision, every interaction reflects our commitment to excellence.'
    },
    {
      icon: 'handshake',
      title: 'Partnership',
      description: 'We\'re not vendors—we\'re partners. Your success is our success, and we invest ourselves fully in every engagement.'
    },
    {
      icon: 'lightbulb',
      title: 'Innovation',
      description: 'Technology moves fast. We stay ahead of the curve, bringing cutting-edge solutions to complex problems.'
    },
    {
      icon: 'visibility',
      title: 'Transparency',
      description: 'No black boxes. We communicate openly, share progress frequently, and involve you in every major decision.'
    }
  ];

  return (
    <main className="flex-grow">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-white/5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 relative">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium text-gray-300 mb-6">
              <span className="material-symbols-outlined text-lg">groups</span>
              About Nurrevo
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              We Build the Future, 
              <span className="text-white/90"> One Line at a Time</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Since 2012, we've been helping ambitious companies turn their boldest ideas into reality. Our team of senior engineers combines deep technical expertise with genuine care for your success.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">Our Mission</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-6">
                Empowering Businesses Through Technology
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                We believe that great software has the power to transform businesses and change lives. That's why we've dedicated ourselves to building solutions that don't just work—they excel.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Our mission is simple: to be the technology partner that every ambitious company deserves. One that combines world-class technical skills with genuine partnership and a relentless focus on outcomes.
              </p>
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="bg-gray-50 p-6 rounded-xl">
                  <div className="text-3xl font-bold text-gray-900 mb-1">150+</div>
                  <div className="text-gray-600">Projects Delivered</div>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <div className="text-3xl font-bold text-gray-900 mb-1">98%</div>
                  <div className="text-gray-600">Client Retention</div>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <div className="text-3xl font-bold text-gray-900 mb-1">40+</div>
                  <div className="text-gray-600">Team Members</div>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <div className="text-3xl font-bold text-gray-900 mb-1">12+</div>
                  <div className="text-gray-600">Years Experience</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              What We Stand For
            </h2>
            <p className="text-lg text-gray-600">
              These principles guide everything we do, from how we build software to how we build relationships.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300">
                <div className="w-14 h-14 rounded-xl bg-gray-900 flex items-center justify-center text-white mb-6 shadow-lg">
                  <span className="material-symbols-outlined text-2xl">{value.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">Leadership Team</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Meet the People Behind the Code
            </h2>
            <p className="text-lg text-gray-600">
              Our leadership team brings decades of experience from the world's top technology companies.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="group text-center">
                <div className="relative mb-6 overflow-hidden rounded-2xl">
                  <img 
                    src={member.image}
                    alt={member.name}
                    className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                    <p className="text-white text-sm">{member.bio}</p>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                <p className="text-gray-600 font-medium">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=600&fit=crop"
                  alt="Modern office"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">Why Nurrevo</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-6">
                The Nurrevo Difference
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-700 text-2xl">verified</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Senior Engineers Only</h4>
                    <p className="text-gray-600">Every team member has 8+ years of production experience. No juniors learning on your project.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-700 text-2xl">trending_up</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Results-Focused</h4>
                    <p className="text-gray-600">We measure success by your business outcomes, not lines of code. ROI is always the goal.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-700 text-2xl">support</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Long-Term Partnership</h4>
                    <p className="text-gray-600">We're here for the journey, not just the project. Ongoing support and scaling are part of the relationship.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-700 text-2xl">schedule</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">On-Time Delivery</h4>
                    <p className="text-gray-600">98% of our projects are delivered on schedule. Your deadlines are our deadlines.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Work with the Best?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join the 150+ companies who have trusted Nurrevo to build their most critical software. Let's discuss how we can help you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-gray-900 bg-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Get in Touch
              <span className="material-symbols-outlined text-xl ml-2">arrow_forward</span>
            </Link>
            <Link
              to="/case-studies"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200"
            >
              See Our Work
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
